import { yupResolver } from '@hookform/resolvers/yup';
import { Button, CustomText, Input, Select } from '@vfit/shared/atoms';
import {
  AppMobile,
  getItalianMunicipalities,
  getItalianProvinces,
  ICountry,
  IProvince,
} from '@vfit/shared/data-access';
import { fonts } from '@vfit/shared/themes';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { IAddressManualConfig, IPicked } from './addressManual.models';
import {
  AddressManualContainer,
  AddressManualInputContainer,
  AddressManualTitle,
  ErrorMsg,
} from './addressManual.style';
import {
  checkIsValidCity,
  checkIsValidProvince,
  retrieveAddressObjectFromString,
  retrieveAddressString,
} from './addressManual.utils';
import { schema } from './validation';
import { IInputs } from '../../../../coverageToolModal.models';

const AddressManual = ({
  cmsConfig,
  onSubmit,
  state,
  inputVarian,
  buttonVariant,
  handleTrackError,
}: IAddressManualConfig) => {
  const italianMunicipalities: ICountry[] = getItalianMunicipalities();
  const italianProvinces: IProvince[] = getItalianProvinces();
  const [isProvinceSelected, setIsProvinceSelected] = useState<boolean>(true);
  const [cityProvince, setCityProvince] = useState<string>(state?.city || '');
  const addressCardLabel = cmsConfig?.addressInputCard;
  const {
    watch,
    setValue,
    getValues,
    trigger,
    register,
    setError,
    clearErrors,
    resetField,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema(cmsConfig?.addressErrors)),
    defaultValues: state,
    mode: 'onChange',
  });
  const displayStateOrProvinceValue = watch('stateOrProvince');

  const calculatedItalianMunicipalities = useMemo(
    () =>
      italianMunicipalities
        .filter((city) =>
          displayStateOrProvinceValue !== '' ? city.province === displayStateOrProvinceValue : true
        )
        .map((municipality) => `${municipality.municipality} (${municipality.province})`),
    [displayStateOrProvinceValue]
  );

  const calculatedItalianProvinces = useMemo(
    () => italianProvinces.map((province) => `${province.long} (${province.short})`),
    [italianMunicipalities]
  );

  const isValidInputs = () => {
    const inputs = getValues();
    const cityRegex = /^[a-zA-Z'àèìòùÀÈÌÒÙáéíóúýÁÉÍÓÚÝ.\s-]*$/g;
    const postalCodeRegex = /^\d{5}$/;
    const stateOrProvinceRegex = /^[a-zA-Z'àèìòùÀÈÌÒÙáéíóúýÁÉÍÓÚÝ\s-]*$/g;
    const streetRegex = /^[a-zA-Z'àèìòùÀÈÌÒÙáéíóúýÁÉÍÓÚÝ.\s-]*$/g;
    const streetNumberRegex = /^[1-9]([0-9]{0,5})(\/?)[a-zA-Z]?$/;

    return (
      inputs?.city &&
      cityRegex.test(inputs.city.trim()) &&
      inputs?.street &&
      streetRegex.test(inputs.street.trim()) &&
      inputs?.streetNumber &&
      streetNumberRegex.test(inputs.streetNumber.trim()) &&
      inputs?.stateOrProvince &&
      stateOrProvinceRegex.test(inputs.stateOrProvince.trim()) &&
      inputs?.postalCode &&
      postalCodeRegex.test(inputs.postalCode.trim())
    );
  };

  const onSelectProvince = (value: string) => {
    setValue('stateOrProvince', (value.match(/\((.*?)\)/) || '')[1]);
    if (cityProvince !== getValues().stateOrProvince) {
      setValue('city', '');
      setCityProvince('');
    }
    trigger('stateOrProvince');
    setIsProvinceSelected(value !== '');
  };

  const onSelectCity = (value: string) => {
    setValue('city', (value.match(/^[^(]+/) || ' ')[0].trim());
    if (value !== '') {
      setCityProvince((value.match(/\((.*?)\)/) || '')[1]);
    }
    trigger('city');
  };

  const checkAddress = (address: IInputs): boolean => {
    let isValidAddress = true;
    Object?.keys(address)?.forEach((key) => {
      if (address[key] === '') {
        resetField(key as IPicked);
        isValidAddress = false;
      } else {
        setValue(key as IPicked, address?.[`${key}`]);
      }
    });
    return isValidAddress;
  };

  const handleValidateAddress = async (): Promise<IInputs> => {
    const inputs = getValues();
    const formattedAddress = retrieveAddressString(inputs);

    const address = await retrieveAddressObjectFromString(formattedAddress, setError, errors);
    const picked = address?.picked;
    const pickedForMutation = address?.pikedForMutation;

    const googleAddress = {
      city: inputs?.city || picked?.city,
      postalCode: inputs?.postalCode || picked?.postalCode,
      stateOrProvince: inputs?.stateOrProvince || picked?.stateOrProvince,
      street: inputs?.street || picked?.street,
      streetNumber: inputs?.streetNumber || picked?.streetNumber,
      region: pickedForMutation?.region || '',
      placeId: pickedForMutation?.placeId || '',
      latitude: pickedForMutation?.latitude?.toString() || '',
      longitude: pickedForMutation?.longitude?.toString() || '',
    } as unknown as IInputs;

    return googleAddress;
  };

  const handleSubmit = async () => {
    const googleAddress = await handleValidateAddress();
    if (googleAddress && checkAddress(googleAddress)) {
      if (isValidInputs() && Object.keys(googleAddress).length >= 3) {
        onSubmit(googleAddress);
      }
    }
    clearErrors();
  };

  const getApiErrorMessage = (): string => {
    let message = '';
    Object.keys(errors).forEach((errorKey) => {
      if (errors[errorKey].message) {
        if (message === '') message = errors[errorKey].message;
      }
    });
    if (message === '') message = "L'indirizzo inserito non è valido";
    return message;
  };

  return (
    <AddressManualContainer>
      {addressCardLabel?.title && (
        <AddressManualTitle>{addressCardLabel?.title}</AddressManualTitle>
      )}
      {addressCardLabel?.description && (
        <CustomText
          text={addressCardLabel?.description}
          textAlign="center"
          size={20}
          lineHeight={26}
          fontFamily={fonts.regular}
          modal
        />
      )}
      <AddressManualInputContainer>
        <Input
          data-cs-mask=""
          variant={inputVarian}
          error={errors?.['street']?.message as string}
          label={addressCardLabel?.labels?.street}
          {...register('street')}
          onError={() => handleTrackError?.('street')}
        />
        <Input
          data-cs-mask=""
          error={errors?.['streetNumber']?.message as string}
          label={addressCardLabel?.labels?.streetNumber}
          {...register('streetNumber')}
          onError={() => handleTrackError?.('streetNumber')}
        />
        <Select
          data-cs-mask=""
          variant={inputVarian}
          error={errors?.['displayStateOrProvince']?.message as string}
          items={calculatedItalianProvinces}
          label={addressCardLabel?.labels?.displayStateOrProvince}
          editable
          {...register('stateOrProvince', {
            onChange: (event) => {
              if (checkIsValidProvince(event?.target?.value, italianProvinces)) {
                onSelectProvince(event?.target?.value);
              }
            },
          })}
          onError={() => handleTrackError?.('stateOrProvince')}
        />
        <Select
          data-cs-mask=""
          variant={inputVarian}
          disabled={!isProvinceSelected}
          error={errors?.['city']?.message as string}
          items={calculatedItalianMunicipalities}
          label={addressCardLabel?.labels?.city}
          editable
          {...register('city', {
            onChange: (event) => {
              if (checkIsValidCity(event?.target?.value, italianMunicipalities)) {
                onSelectCity(event?.target?.value);
                setCityProvince((event?.target?.value?.match(/\((.*?)\)/) || '')[1]);
              }
            },
          })}
          onError={() => handleTrackError?.('city')}
        />
        <Input
          data-cs-mask=""
          variant={inputVarian}
          error={errors?.postalCode?.message as string}
          label={addressCardLabel?.labels?.postalCode}
          {...register('postalCode')}
          onError={() => handleTrackError?.('postalCode')}
        />
      </AddressManualInputContainer>
      {!isValidInputs() && Object.keys(errors).length > 0 && (
        <ErrorMsg>{getApiErrorMessage()}</ErrorMsg>
      )}
      <div style={{ marginTop: 25 }}>
        <Button
          onClick={handleSubmit}
          disabled={!isValidInputs()}
          variant={AppMobile.checkIsApp() ? 'mva:primary' : buttonVariant}
        >
          {addressCardLabel?.buttonLabel}
        </Button>
      </div>
    </AddressManualContainer>
  );
};

export default AddressManual;
